import type { ProjectSettings } from '$lib/model/types';
import { pixelDimensions } from './settings-utils';

export interface Tile {
  row: number;       // 1-based
  col: number;       // 1-based
  x_px: number;      // top-left X in project pixel space
  y_px: number;      // top-left Y in project pixel space
  width_px: number;
  height_px: number;
}

export interface TileGrid {
  cols: number;
  rows: number;
  tiles: Tile[];
}

// Compute the tile grid for the current project. Returns a single tile
// covering the whole project when tiling is off or unneeded.
export function computeTiles(settings: ProjectSettings): TileGrid {
  const dims = pixelDimensions(settings);
  if (settings.tile_mode === 'never') {
    return {
      cols: 1,
      rows: 1,
      tiles: [
        {
          row: 1,
          col: 1,
          x_px: 0,
          y_px: 0,
          width_px: dims.width,
          height_px: dims.height,
        },
      ],
    };
  }

  // Convert paper and overlap from inches to pixels via project DPI.
  const paperW = Math.max(
    1,
    Math.round(settings.paper_width_in * settings.dpi),
  );
  const paperH = Math.max(
    1,
    Math.round(settings.paper_height_in * settings.dpi),
  );
  const overlapPx = Math.max(
    0,
    Math.round(settings.tile_overlap_in * settings.dpi),
  );

  // If project fits in one paper, no tiling.
  if (dims.width <= paperW && dims.height <= paperH) {
    return {
      cols: 1,
      rows: 1,
      tiles: [
        {
          row: 1,
          col: 1,
          x_px: 0,
          y_px: 0,
          width_px: dims.width,
          height_px: dims.height,
        },
      ],
    };
  }

  const stepX = paperW - overlapPx;
  const stepY = paperH - overlapPx;
  if (stepX <= 0 || stepY <= 0) {
    // Overlap is larger than paper; treat as no-tiling fallback.
    return {
      cols: 1,
      rows: 1,
      tiles: [
        {
          row: 1,
          col: 1,
          x_px: 0,
          y_px: 0,
          width_px: dims.width,
          height_px: dims.height,
        },
      ],
    };
  }

  const cols = Math.max(
    1,
    Math.ceil((dims.width - overlapPx) / stepX),
  );
  const rows = Math.max(
    1,
    Math.ceil((dims.height - overlapPx) / stepY),
  );

  // Every tile is a full paper-size sheet. The last column/row of tiles
  // contains empty (white) space past the project edge — the user prints
  // the full 8.5×11 sheet regardless and tapes them together.
  const tiles: Tile[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      tiles.push({
        row: r + 1,
        col: c + 1,
        x_px: c * stepX,
        y_px: r * stepY,
        width_px: paperW,
        height_px: paperH,
      });
    }
  }
  return { cols, rows, tiles };
}
